import { toast } from 'sonner';

import { isOrpcNotFoundError } from '@/_shared/lib/orpc-errors';

import type { DashboardArtifactOpenHint } from './dashboard-draft';

export interface OpenDashboardArtifactDeps {
  hydrateSavedAndOpen: (slug: string, hint: { title: string; dashboardId?: string }) => void;
  hydrateNotFoundAndOpen: (slug: string, hint: { title: string }) => void;
  setActive: (slug: string) => void;
  fetchDashboard: (slug: string) => Promise<{ title: string; id: string } | null>;
  onError?: (message: string) => void;
}

/**
 * Open a dashboard artifact from a transcript card or Artifacts menu.
 * Resolves dashboard id via DB when missing; stale-request guard via `isCurrentRequest`.
 */
export async function openDashboardArtifactFromHint(
  slug: string,
  hint: DashboardArtifactOpenHint | undefined,
  existing: { saved: unknown; externallyRemoved?: boolean } | undefined,
  isCurrentRequest: () => boolean,
  deps: OpenDashboardArtifactDeps,
): Promise<void> {
  if (hint?.saved && (!existing || existing.externallyRemoved)) {
    if (hint.dashboardId) {
      deps.hydrateSavedAndOpen(slug, {
        title: hint.title ?? slug,
        dashboardId: hint.dashboardId,
      });
      return;
    }

    try {
      const dashboard = await deps.fetchDashboard(slug);
      if (!isCurrentRequest()) return;
      if (dashboard) {
        deps.hydrateSavedAndOpen(slug, {
          title: dashboard.title,
          dashboardId: dashboard.id,
        });
        return;
      }
      deps.hydrateNotFoundAndOpen(slug, { title: hint.title ?? slug });
    } catch (err) {
      if (!isCurrentRequest()) return;
      if (isOrpcNotFoundError(err)) {
        deps.hydrateNotFoundAndOpen(slug, { title: hint.title ?? slug });
        return;
      }
      const message = err instanceof Error ? err.message : 'Could not load dashboard';
      // Notify once: prefer the caller's own error surface, else fall back to a toast (avoid double).
      if (deps.onError) deps.onError(message);
      else toast.error(message || 'Could not load dashboard');
      // Transient (non-not-found) failure: do NOT downgrade a known-saved dashboard to an unsaved
      // draft. hydrateSavedAndOpen with no id sets `saved: null`, which renders a "Save" button whose
      // click tries to CREATE a new dashboard for an already-saved slug (fails slug-uniqueness).
      // Reopen a cached entry if we have one; otherwise leave the panel and let the user retry.
      if (existing) deps.setActive(slug);
    }
    return;
  }

  deps.setActive(slug);
}
